import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { FC } from "react";
import { Dialog, Heading } from "react-aria-components";
import { ImprovementRate } from "../insights/improvementRate.js";
import { CriterionAverageScore } from "../teammatePerformance/criterionAverageScore.js";
import type {
  ChapterWithCriterionScore,
  CriterionInsightsWithImprovementRate,
} from "./trainingInsightsHelpers.js";

export interface CriterionImprovementRateDialogProps {
  criterion: PlaybookCriterionNode;
  insights: CriterionInsightsWithImprovementRate;
}

export const CriterionImprovementRateDialog: FC<
  CriterionImprovementRateDialogProps
> = (props) => {
  const { criterion, insights } = props;

  const renderChapterLabel = (chapter: ChapterWithCriterionScore) => (
    <>
      Chapter {chapter.index + 1}
      {chapter.topic ? (
        <>
          {". "}
          {chapter.topic}
        </>
      ) : null}
    </>
  );

  return (
    <Dialog className="max-w-md space-y-3 p-3 text-sm">
      <Heading slot="title" className="truncate text-base font-semibold">
        {criterion.name}
      </Heading>

      <div className="grid grid-cols-[1fr_auto] gap-4">
        <div className="col-span-full grid grid-cols-subgrid items-center gap-y-0.5">
          <div className="col-span-full font-semibold whitespace-nowrap">
            First covered in:
          </div>

          <div className="truncate">
            {renderChapterLabel(insights.firstChapter)}
          </div>

          <CriterionAverageScore
            valueType={criterion.grading_value_type}
            value={insights.firstChapter.value}
          />
        </div>

        <div className="col-span-full grid grid-cols-subgrid items-center gap-y-0.5">
          <div className="col-span-full font-semibold whitespace-nowrap">
            Last covered in:
          </div>

          <div className="truncate">
            {renderChapterLabel(insights.lastChapter)}
          </div>

          <div className="flex items-center gap-2">
            <CriterionAverageScore
              valueType={criterion.grading_value_type}
              value={insights.lastChapter.value}
            />
            <ImprovementRate rate={insights.improvementRate} />
          </div>
        </div>
      </div>

      <div className="text-muted-foreground">
        Improvement over the first chapter to cover this topic.
      </div>
    </Dialog>
  );
};
